/* Équipe et caisses › « Vos caisses » (Version 19; Compte et accès dissolves, errata B1: the tills leave it for here, their
   registering for the « Valider un cadeau » door, routes/equipe/Caisse.tsx; v3.3 migration WF-033: the tab, its search,
   the État filter, « Appareils enregistrés » + « Enregistrer une caisse », « Ouvrir » beside « Retirer »).

   The tills in scope (GET /v1/till/devices, contract 08 §8.4; D-31: inside an establishment, its tills only). A till taken
   out no longer validates anything: the list shows the live ones, the État filter « Révoquée » shows the others as the API
   still lists them. « Verrouillée » is the till's own state after wrong codes (the player's till, contract 08 §8.1): the
   API's device row carries no lock field yet, so that filter finds nothing until it does — nothing here invents one, and the
   rule line names no figure the app has no source for. The list never carries a token. `lastUsedAt` is a till's last use
   (a code personnel, a lookup or a validation), so the line says « utilisation ».
   « Retirer » (POST …/revoke) stops a till at its next call; it is the owner's, an admin's or a manager's in scope (the
   API's 403 forbidden_role says the same). A refusal says the app's words (`tillRefusal`). Staff do not see the section: the
   API answers them 403. « Ouvrir » is the door (/caisse): the till opens on this device, registered or marked first. */
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Button, Card, Field, Input, List, ListRow, Modal, Note, Select, useToast } from '@/ui'
import { useLocations } from '@/lib/api'
import { ALL, allowedLocations, currentUser, useScope } from '@/lib/scope'
import { useRevokeTillDevice, useTillDevices, type TillDevice } from '@/routes/parametres/api'
import { setTillMark, tillMark, tillRefusal } from './Caisse'

const day = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', timeZone: 'Europe/Paris' })
type Etat = 'all' | 'disponible' | 'verrouillee' | 'revoquee'

export default function VosCaisses() {
  const toast = useToast()
  const nav = useNavigate()
  const [scope] = useScope()
  const staff = currentUser().role === 'staff'
  const { data: places } = useLocations()
  const { data: tills, isPending, isError } = useTillDevices(!staff)
  const revoke = useRevokeTillDevice()
  const [toRevoke, setToRevoke] = useState<TillDevice | null>(null)
  const [q, setQ] = useState('')
  const [etat, setEtat] = useState<Etat>('all')

  if (staff) return null
  const mine = allowedLocations(places ?? [])
  const many = scope === ALL && mine.length > 1
  const placeName = (id: string) => places?.find(l => l.id === id)?.name ?? id
  const mark = tillMark()
  const inScope = (tills ?? []).filter(t => scope === ALL || t.locationId === scope)
  const needle = q.trim().toLocaleLowerCase('fr-FR')
  /* « Verrouillée »: no lock field on the device row (parametres/api TillDevice) — the filter stands, finds nothing yet */
  const shown = inScope.filter(t =>
    (!needle || `${t.name} ${placeName(t.locationId)}`.toLocaleLowerCase('fr-FR').includes(needle))
    && (etat === 'all' ? !t.revokedAt : etat === 'disponible' ? !t.revokedAt : etat === 'revoquee' ? !!t.revokedAt : false))

  const doRevoke = () => {
    if (!toRevoke || revoke.isPending) return
    const t = toRevoke
    revoke.mutate(t.id, {
      onSuccess: () => { if (tillMark() === t.id) setTillMark(null); setToRevoke(null); toast({ message: 'Caisse retirée.' }) },
      onError: e => toast({ message: tillRefusal(e) }),
    })
  }
  const stateLine = (t: TillDevice) => t.revokedAt ? `Révoquée le ${day(t.revokedAt)}` : `Disponible${mark === t.id ? ' · appareil sélectionné' : ''}`

  return (
    <div className="sect" data-testid="vos-caisses" style={{ gap: 16 }}>
      <Card data-testid="caisses-filtres">
        <div className="fields-row" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))' }}>
          <Field label="Rechercher une caisse">{id => <Input id={id} value={q} placeholder="Nom, mot-clé…" onChange={e => setQ(e.target.value)} />}</Field>
          <Field label="État">{id => (
            <Select id={id} value={etat} onChange={e => setEtat(e.target.value as Etat)}>
              <option value="all">Toutes les caisses</option>
              <option value="disponible">Disponible</option>
              <option value="verrouillee">Verrouillée</option>
              <option value="revoquee">Révoquée</option>
            </Select>
          )}</Field>
        </div>
        <p className="f-help" style={{ margin: '8px 0 0' }} data-testid="caisses-resultats">{shown.length} résultat{shown.length > 1 ? 's' : ''}</p>
      </Card>

      <Card>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
          <h2 className="t-section" style={{ margin: 0 }}>Appareils enregistrés</h2>
          <span style={{ marginLeft: 'auto' }}><Button size="sm" onClick={() => nav('/caisse?enregistrer=1')}>Enregistrer une caisse</Button></span>
        </div>
        {isPending ? <p className="f-help" style={{ margin: 0 }}>Chargement…</p>
          : isError ? <Note tone="crit">La liste des caisses n’a pas pu être chargée. Réessayez dans un instant.</Note>
          : shown.length === 0
            ? <p className="f-help" style={{ margin: 0 }}>{inScope.some(t => !t.revokedAt) ? 'Aucune caisse ne correspond.' : 'Aucune caisse enregistrée.'}</p>
            : (
              <List>
                {shown.map(t => (
                  <ListRow key={t.id} title={t.name}
                    meta={[
                      stateLine(t),
                      many ? placeName(t.locationId) : null,
                      `enregistrée le ${day(t.createdAt)}`,
                      t.lastUsedAt ? `dernière utilisation le ${day(t.lastUsedAt)}` : 'jamais utilisée',
                    ].filter(Boolean).join(' · ')}
                    end={t.revokedAt ? undefined : <span style={{ display: 'inline-flex', gap: 8 }}>
                      <Button size="sm" onClick={() => nav('/caisse')}>Ouvrir</Button>
                      <Button size="sm" variant="quiet" onClick={() => setToRevoke(t)}>Retirer</Button>
                    </span>} />
                ))}
              </List>
            )}
        <p className="f-help" style={{ margin: '10px 0 0' }}>Une caisse est un appareil. Chaque personne utilise son propre code. Pour enregistrer un appareil comme caisse, ouvrez <Link to="/caisse">Valider un cadeau</Link> sur cet appareil.</p>
      </Card>

      <Modal open={toRevoke !== null} title={`Retirer «\u00a0${toRevoke?.name ?? ''}\u00a0»\u00a0?`} onClose={() => setToRevoke(null)}
        actions={<>
          <Button size="sm" variant="quiet" onClick={() => setToRevoke(null)}>Annuler</Button>
          <Button size="sm" variant="danger" disabled={revoke.isPending} onClick={doRevoke}>Retirer</Button>
        </>}>
        <p className="t-body">Cet appareil ne pourra plus valider de cadeau. Pour le réutiliser, enregistrez-le de nouveau depuis «&nbsp;Valider un cadeau&nbsp;».</p>
      </Modal>
    </div>
  )
}
